import { css } from '@emotion/react';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { ButtonStyle } from '~/_shared/baseComponents/buttons';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import { TooltipComponent } from '~/_shared/baseComponents/tooltip/tooltip.component';
import { useIsBoundaryToolReady } from '~/sidebar/sidebarApps/mapTools/boundary/activeBoundary/edit/useIsBoundaryToolReady';
import { ModalComponent } from '../../../../../_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '../../../../../_shared/components/overlay/overlayLoader.component';
import { type SpreadsheetColumnId } from '../../../../../_shared/types/spreadsheetData/spreadsheetColumn';
import { useTranslation } from '../../../../../_shared/utils/hooks';
import { type ModalProps } from '../../../../../modal/modalType.enum';
import {
  BoundaryTerritoryType, type CalculateBucketFunctionNumeric,
} from '../../../../../store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { ActiveBoundaryFillEditComponent } from '../activeBoundary/edit/activeBoundaryFillEditComponent';
import { useActiveBoundaryFillEdit } from '../activeBoundary/edit/useActiveBoundaryFillEdit';

type CustomizeBoundaryFillProps = ModalProps<{
  boundaryFillData: SpreadsheetColumnId | null;
  boundaryTerritoryType?: BoundaryTerritoryType | null;
  boundaryGroupId: number;
  initialDemographicId: string | null;
  rangeCount?: number | null;
  isLoading: boolean;
  initialNumericCalculateBucketFunction: CalculateBucketFunctionNumeric | null;
  onConfirm: (fillConfig: Readonly<{
    boundaryTerritoryType: BoundaryTerritoryType;
    boundaryFillData: SpreadsheetColumnId | null;
    demographicId: string | null;
    numericCalculateBucketFunction: CalculateBucketFunctionNumeric | null;
    numberOfRanges: number;
  }>, doNotClose: boolean) => void;
  title: string;
  fixedRangeCount?: number;
}>;

const editWrapperStyle = css({
  padding: '5px 16px',
});

export const CustomizeBoundaryFillComponent: FC<CustomizeBoundaryFillProps> = ({
  boundaryFillData,
  boundaryGroupId,
  boundaryTerritoryType,
  fixedRangeCount,
  initialDemographicId,
  initialNumericCalculateBucketFunction,
  isLoading,
  isOpen,
  onClose,
  onConfirm,
  rangeCount,
  title,
}) => {
  const { isBoundaryToolPreparing, boundaryToolDisabledReason } = useIsBoundaryToolReady();
  const {
    isUpdateDisabled, boundaryFillModel, ...boundaryFillProps
  } = useActiveBoundaryFillEdit({
    initialBoundaryTerritoryType: boundaryTerritoryType,
    initialBoundaryFillData: boundaryFillData,
    initialDemographicId,
    boundaryGroupId,
    initialNumericCalculateBucketFunction,
    initialRangeCount: rangeCount,
    fixedRangeCount,
  });
  const [t] = useTranslation();

  const onUpdateSubmit = (doNotClose: boolean) => {
    const { boundaryTerritoryType } = boundaryFillModel;
    if (isUpdateDisabled || boundaryTerritoryType === null || isBoundaryToolPreparing) {
      return;
    }

    onConfirm({
      ...boundaryFillModel,
      boundaryTerritoryType,
      demographicId: boundaryFillModel.selectedDemographicId,
    }, doNotClose);
  };

  const resetForm = () => {
    const { boundaryTerritoryType } = boundaryFillModel;
    const {
      onBoundaryTerritoryTypeChange,
      onBoundaryFillDataChange,
      onDemographicIdChange,
      onNumericCalculateBucketFunctionChange,
      onSelectedDemographicCategoryChange,
    } = boundaryFillProps;
    switch (boundaryTerritoryType) {
      case BoundaryTerritoryType.Numeric:{
        onBoundaryFillDataChange(null);
        onDemographicIdChange(null);
        onSelectedDemographicCategoryChange(null);
        break;
      }
      case BoundaryTerritoryType.Groups:
      case BoundaryTerritoryType.Demographic: {
        onBoundaryFillDataChange(null);
        onDemographicIdChange(null);
        onSelectedDemographicCategoryChange(null);
        onNumericCalculateBucketFunctionChange(null);
        break;
      }
      default:
        // Includes Manual and Density
        onBoundaryTerritoryTypeChange(null);
        onBoundaryFillDataChange(null);
        onDemographicIdChange(null);
        onSelectedDemographicCategoryChange(null);
        onNumericCalculateBucketFunctionChange(null);
    }
  };

  const onSaveAndResetClick = () => {
    onUpdateSubmit(true);
    resetForm();
  };

  return (
    <ModalComponent
      onClose={onClose}
      isOpen={isOpen}
      caption={title}
      confirmButton={(
        <TooltipComponent
          tooltipContent={boundaryToolDisabledReason}
        >
          <ButtonComponent
            prefixIcon={faCheck}
            text={t('Save')}
            onClick={() => onUpdateSubmit(false)}
            isDisabled={isUpdateDisabled || isLoading || isBoundaryToolPreparing}
          />
        </TooltipComponent>
      )}
      leftFooterContent={(
        <>
          {isLoading ? <OverlayLoaderComponent /> : null}
          <ButtonComponent
            buttonStyle={ButtonStyle.Secondary}
            isDisabled={isUpdateDisabled || isLoading || isBoundaryToolPreparing}
            onClick={onSaveAndResetClick}
            text={t('Save and Create Another')}
          />
        </>
      )}
    >
      <div css={editWrapperStyle}>
        <ActiveBoundaryFillEditComponent
          labelCountStart={1}
          {...boundaryFillProps}
          {...boundaryFillModel}
        />
      </div>
    </ModalComponent>
  );
};
